import { BankOperationRelatedTypes } from 'constants/bankOperationTypes';

import { useGetList } from 'react-admin';
import { Controller, useFormContext } from 'react-hook-form';
import { DEFAULT_DATE_MASK } from 'shared/mui/DatePicker/config/defaultValues';
import { DateTimeRangeTimezonePicker } from 'shared/mui/DatePicker/DateTimeRangeTimezonePicker/DateTimeRangeTimezonePicker';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { useFilterTimezone } from 'shared/mui/Filter/hooks/useFilterTimezone';
import { AutocompleteMultiselectInput, EnumAutocompleteInput } from 'shared/react-admin/Inputs';
import { ReferenceArrayInput } from 'shared/react-admin/ReferenceInputs';
import { BankOperationType } from 'types/embedded/bankOperation';
import { Referral } from 'types/embedded/referral';
import { User } from 'types/embedded/user';
import { getFilteredBankOpsTypes } from 'utils/getFilteredBankOpsTypes';

import { useBankOperationStatuses } from './useBankOperationStatuses';

export const useBankOperationsFilterFields = (): FilterFieldConfig[] => {
  const { control, watch, getValues, setValue, resetField } = useFormContext();
  const { timezone, onChangeTimezone } = useFilterTimezone();
  const { setAppliedFilter, appliedFilters } = useFilterContext();
  const [merchant, provider] = watch(['merchant', 'provider']);
  const { data: bankOpsTypes = [] } = useGetList<BankOperationType>('bank_operation_types', {
    filter: { groups: ['reference'] },
  });

  const { bankOperationStatuses } = useBankOperationStatuses();

  return [
    {
      name: 'appliedDate',
      label: 'Дата в ПС / период',
      chipType: 'dateTimeRangeTimezone',
      chipProps: {
        mask: DEFAULT_DATE_MASK,
      },
      input: (
        <Controller
          control={control}
          name="appliedDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <DateTimeRangeTimezonePicker
              label="Дата в ПС / период"
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              onChangeTimezone={onChangeTimezone}
              timezone={timezone}
              value={value}
            />
          )}
        />
      ),
    },
    {
      name: 'canceledDate',
      label: 'Дата аннулирования / период',
      chipType: 'dateTimeRangeTimezone',
      chipProps: {
        mask: DEFAULT_DATE_MASK,
        timezoneName: 'timezone',
      },
      input: (
        <Controller
          control={control}
          name="canceledDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <DateTimeRangeTimezonePicker
              label="Дата аннулирования / период"
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              onChangeTimezone={onChangeTimezone}
              timezone={timezone}
              value={value}
            />
          )}
        />
      ),
    },
    {
      name: 'approveDate',
      label: 'Дата согласования / период',
      chipType: 'dateTimeRangeTimezone',
      chipProps: {
        mask: DEFAULT_DATE_MASK,
        timezoneName: 'timezone',
      },
      input: (
        <Controller
          control={control}
          name="approveDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <DateTimeRangeTimezonePicker
              label="Дата согласования / период"
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              onChangeTimezone={onChangeTimezone}
              timezone={timezone}
              value={value}
            />
          )}
        />
      ),
    },
    {
      name: 'referral',
      label: 'Рефералы',
      chipType: 'multiselect',
      chipProps: {
        resource: 'referrals',
        limitTags: 1,
        renderChipLabel: (record: Referral) => `${record?.refId}: ${record?.name}`,
      },
      input: (
        <ReferenceArrayInput
          filter={{ merchants: merchant, provider }}
          perPage={100}
          reference="referrals"
          source="referral"
        >
          <AutocompleteMultiselectInput
            getOptionLabel={(record: Referral) => `${record?.refId}: ${record?.name}`}
            helperText={false}
            label="Рефералы"
            limitTags={1}
            name="referral"
            size="small"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'merchant',
      label: 'Мерчанты',
      chipType: 'multiselect',
      chipProps: {
        limitTags: 1,
        resource: 'merchants',
        onApply: () => {
          setAppliedFilter('provider', []);
          setAppliedFilter('merchantAccount', []);

          resetField('provider', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
        },
        onDelete: () => {
          resetField('provider', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });

          setAppliedFilter('provider', []);
          setAppliedFilter('merchantAccount', []);
        },
        onReset: () => {
          setValue('provider', appliedFilters?.['provider']);
          setValue('merchantAccount', appliedFilters?.['merchantAccount']);

          setAppliedFilter('provider', appliedFilters?.['provider']);
          setAppliedFilter('merchantAccount', appliedFilters?.['merchantAccount']);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          filter={{ exists: { merchantAccounts: true } }}
          perPage={100}
          reference="merchants"
          source="merchant"
        >
          <AutocompleteMultiselectInput
            helperText={false}
            label="Мерчанты"
            limitTags={1}
            name="merchant"
            {...(variant === 'field' && {
              onChange: () => {
                setValue('provider', []);
                setValue('merchantAccount', []);
              },
            })}
            optionText="name"
            size="small"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'provider',
      label: 'Провайдеры',
      chipType: 'multiselect',
      chipProps: {
        limitTags: 1,
        resource: 'providers',
        onApply: () => {
          setAppliedFilter('merchantAccount', []);
          resetField('merchantAccount', { keepDirty: true });
        },
        onDelete: () => {
          resetField('merchantAccount', { keepDirty: true });
          setAppliedFilter('merchantAccount', []);
        },
        onReset: () => {
          setValue('merchantAccount', appliedFilters?.['merchantAccount']);
          setAppliedFilter('merchantAccount', appliedFilters?.['merchantAccount']);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          filter={{ merchantAccounts: { merchants: merchant } }}
          perPage={100}
          reference="providers"
          sort={{ field: 'name', order: 'ASC' }}
          source="provider"
        >
          <AutocompleteMultiselectInput
            helperText={false}
            label="Провайдеры"
            limitTags={1}
            name="provider"
            {...(variant === 'field' && {
              onChange: () => {
                setValue('merchantAccount', []);
              },
            })}
            optionText="name"
            size="small"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'merchantAccount',
      label: 'Счета мерчантов',
      chipType: 'multiselect',
      chipProps: {
        limitTags: 1,
        resource: 'merchant_accounts',
      },
      input: (
        <ReferenceArrayInput
          filter={{ merchants: merchant, provider }}
          perPage={100}
          reference="merchant_accounts"
          source="merchantAccount"
        >
          <AutocompleteMultiselectInput
            helperText={false}
            label="Счета мерчантов"
            limitTags={1}
            name="merchantAccount"
            optionText="name"
            size="small"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'direction',
      label: 'Направление',
      chipType: 'enumAutocomplete',
      chipProps: {
        resource: 'directions',
      },
      input: (
        <EnumAutocompleteInput
          filter={{ groups: ['reference'] }}
          filterToQuery={(searchText: string) => ({ description: searchText })}
          fullWidth
          helperText={false}
          label="Направление"
          name="direction"
          optionText="description"
          optionValue="value"
          resource="directions"
          source="direction"
          variant="outlined"
        />
      ),
    },
    {
      name: 'type',
      label: 'Типы операций',
      chipType: 'enumMultiselect',
      chipProps: {
        limitTags: 1,
        resource: 'bank_operation_types',
      },
      input: (
        <AutocompleteMultiselectInput
          defaultValue={watch('type')}
          helperText={false}
          label="Типы операций"
          limitTags={1}
          name="type"
          optionText="description"
          optionValue="value"
          options={getFilteredBankOpsTypes(bankOpsTypes, [
            BankOperationRelatedTypes.ConversionReferrals,
          ])}
          resource="bank_operation_types"
        />
      ),
    },
    {
      name: 'status',
      label: 'Статусы',
      chipType: 'enumMultiselect',
      chipProps: {
        limitTags: 1,
        resource: 'bank_operation_statuses',
      },
      input: (
        <AutocompleteMultiselectInput
          defaultValue={getValues('status')}
          helperText={false}
          label="Статусы"
          limitTags={1}
          name="status"
          optionText="description"
          optionValue="value"
          options={bankOperationStatuses || []}
        />
      ),
    },
    {
      name: 'user',
      label: 'Согласующие',
      chipType: 'multiselect',
      chipProps: {
        limitTags: 1,
        resource: 'users',
        renderChipLabel: (choice: User) => `${choice.firstName} ${choice.lastName}`,
      },
      input: (
        <ReferenceArrayInput perPage={200} reference="users" source="user">
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({
              fullName: searchText,
            })}
            getOptionLabel={(choice: User) => `${choice.firstName} ${choice.lastName}`}
            helperText={false}
            label="Согласующие"
            name="user"
            size="small"
          />
        </ReferenceArrayInput>
      ),
    },
  ];
};
